import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { SubscriptionService, UserAccountsService } from '../../services'
import {
  hasValidAuthToken,
  isPublicAuthRoute,
} from '../../utils/unauthorizedSession'

const hasActiveSubscriptionResponse = (response) => {
  const data = response?.data

  return (
    response?.responseStatus === 200 &&
    !!data &&
    (!Array.isArray(data) || data.length > 0)
  )
}

const DEFAULT_ELIGIBILITY = {
  isUserWithoutSubscription: false,
  canDownloadCampaigns: false,
}

export function useArtsLimitUpsellEligibility() {
  const { userData } = useAuth()
  const location = useLocation()
  const [isUserWithoutSubscription, setIsUserWithoutSubscription] = useState(false)
  const [canDownloadCampaigns, setCanDownloadCampaigns] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const fetchInFlightRef = useRef(false)

  const refreshEligibility = useCallback(async () => {
    const shouldSkip =
      !userData ||
      !hasValidAuthToken() ||
      isPublicAuthRoute(location.pathname)

    if (shouldSkip) {
      setIsUserWithoutSubscription(false)
      setCanDownloadCampaigns(false)
      setIsLoading(false)
      return DEFAULT_ELIGIBILITY
    }

    if (fetchInFlightRef.current) {
      return DEFAULT_ELIGIBILITY
    }

    fetchInFlightRef.current = true
    setIsLoading(true)

    try {
      const userResponse = await UserAccountsService.getUserAccountsByLogin()
      const canDownloadAll = Boolean(userResponse?.data?.canDownloadCampaigns)

      setCanDownloadCampaigns(canDownloadAll)

      let withoutSubscription = false

      try {
        const activeSubscriptionResponse =
          await SubscriptionService.getActiveSubscription()
        const hasActiveSubscription = hasActiveSubscriptionResponse(
          activeSubscriptionResponse,
        )

        withoutSubscription = !hasActiveSubscription
        setIsUserWithoutSubscription(withoutSubscription)

        if (hasActiveSubscription && !canDownloadAll) {
          setCanDownloadCampaigns(Boolean(userResponse?.data?.canDownloadCampaigns))
        }
      } catch {
        withoutSubscription = false
        setIsUserWithoutSubscription(false)
      }

      return {
        isUserWithoutSubscription: withoutSubscription,
        canDownloadCampaigns: canDownloadAll,
      }
    } catch (error) {
      console.error('Error loading arts limit upsell eligibility:', error)

      return DEFAULT_ELIGIBILITY
    } finally {
      fetchInFlightRef.current = false
      setIsLoading(false)
    }
  }, [location.pathname, userData])

  useEffect(() => {
    refreshEligibility()
  }, [refreshEligibility])

  const shouldUseSubscribedFlow =
    !isUserWithoutSubscription || canDownloadCampaigns

  return {
    isUserWithoutSubscription,
    canDownloadCampaigns,
    shouldUseSubscribedFlow,
    isFlowBEnabled: isUserWithoutSubscription && !canDownloadCampaigns,
    isFlowCEnabled: shouldUseSubscribedFlow,
    isLoading,
    refreshEligibility,
  }
}
